/**
 * Assignment System Data Management (Front-End Only)
 * LocalStorage persistence. Used by Assignment React components.
 * 
 * Supports assigning any item type (Priority Queue Tasks, Quality Alerts, Staffing Alerts, etc.)
 * with workload tracking, configuration, and full assignment history.
 */

export const AssignmentDB = {
  STORAGE_KEYS: {
    ASSIGNMENTS: 'nexonurse_assignments',
    CONFIG: 'nexonurse_assignment_config',
    WORKLOAD_CACHE: 'nexonurse_assignment_workload_cache',
    FEATURE_TOGGLE: 'nexonurse_feature_assignments'
  },

  init() {
    if (localStorage.getItem(this.STORAGE_KEYS.ASSIGNMENTS) == null) {
      localStorage.setItem(this.STORAGE_KEYS.ASSIGNMENTS, JSON.stringify([]));
    }
    if (localStorage.getItem(this.STORAGE_KEYS.CONFIG) == null) {
      this.setDefaultConfig();
    }
    if (localStorage.getItem(this.STORAGE_KEYS.FEATURE_TOGGLE) == null) {
      this.setFeatureToggle({
        enabled: false,
        enabledForRoles: [],
        enabledForUsers: []
      });
    }
  },

  getFeatureToggle() {
    const data = localStorage.getItem(this.STORAGE_KEYS.FEATURE_TOGGLE);
    return data ? JSON.parse(data) : null;
  },

  setFeatureToggle(toggle) {
    localStorage.setItem(this.STORAGE_KEYS.FEATURE_TOGGLE, JSON.stringify(toggle));
  },

  isFeatureEnabled(userRole) {
    const toggle = this.getFeatureToggle();
    if (!toggle) return false;
    if (toggle.enabled) return true;
    return (toggle.enabledForRoles || []).includes(userRole);
  },

  setDefaultConfig() {
    const defaultConfig = {
      assignmentRules: {
        priorityQueueTasks: {
          enabled: true,
          overdueDefinition: { value: 0, unit: 'hours' },
          atRiskDefinition: { value: 24, unit: 'hours' }
        },
        qualityMetrics: {
          enabled: true,
          overdueDefinition: { value: 7, unit: 'days' },
          atRiskDefinition: { type: 'trending', threshold: 5, period: 30, unit: 'days' }
        },
        staffingAlerts: {
          enabled: true,
          overdueDefinition: { value: 4, unit: 'hours' },
          atRiskDefinition: { value: 8, unit: 'hours' }
        },
        accessMetrics: {
          enabled: true,
          overdueDefinition: { consecutiveHours: 6, unit: 'hours' },
          atRiskDefinition: { type: 'trending', direction: 'approaching_threshold' }
        },
        inventory: {
          enabled: true,
          overdueDefinition: { value: 2, unit: 'days' },
          atRiskDefinition: { threshold: 25, unit: 'percent' }
        },
        strategicInitiatives: {
          enabled: true,
          overdueDefinition: { value: 1, unit: 'days' },
          atRiskDefinition: { behindSchedule: 20, unit: 'percent' }
        },
        customTasks: {
          enabled: true,
          requireApproval: false
        }
      },
      workloadThresholds: {
        mode: 'count',
        countThresholds: { yellow: 8, red: 12, critical: 15 },
        pointsThresholds: { yellow: 30, red: 50, critical: 70 },
        taskPoints: {
          priorityQueueTask: 5,
          qualityAlert: 10,
          staffingAlert: 3,
          accessIssue: 5,
          inventory: 1,
          initiativeMilestone: 15,
          customTask: 5
        }
      },
      assignmentPermissions: {
        director: {
          canAssign: {
            priorityQueueTasks: true,
            qualityAlerts: true,
            staffingAlerts: true,
            accessIssues: true,
            inventory: true,
            initiativeMilestones: true,
            customTasks: true
          },
          canAssignTo: { lateral: true, downward: true, upward: true }
        },
        manager: {
          canAssign: {
            priorityQueueTasks: true,
            qualityAlerts: false,
            staffingAlerts: true,
            accessIssues: true,
            inventory: true,
            initiativeMilestones: false,
            customTasks: true
          },
          canAssignTo: { lateral: true, downward: true, upward: false }
        },
        superUser: {
          canAssign: { all: true },
          canAssignTo: { all: true }
        }
      },
      notificationSettings: {
        onAssignment: { inApp: true, email: false, sms: false },
        onApproachingOverload: { notifyAssignee: true, notifyManager: true, blockAssignment: false },
        onOverdue: { notifyAssignee: true, notifyAssigner: true, escalateToManager: true, frequency: 'daily' }
      },
      assignmentMetadata: {
        required: { dueDate: true, priority: true, justification: false, estimatedTime: false, requiredSkills: false },
        priorities: ['low', 'medium', 'high', 'critical'],
        allowReassignment: true,
        allowDecline: true,
        requireDeclineReason: true
      },
      escalationRules: {
        autoEscalate: { enabled: true, conditions: { overdueBy: 48, escalateTo: 'manager' } },
        urgentEscalation: { enabled: true, flagAsCritical: ['qualityAlerts', 'staffingAlerts'], notifyChain: true }
      }
    };
    localStorage.setItem(this.STORAGE_KEYS.CONFIG, JSON.stringify(defaultConfig));
  },

  getConfig() {
    const data = localStorage.getItem(this.STORAGE_KEYS.CONFIG);
    return data ? JSON.parse(data) : null;
  },

  saveConfig(config) {
    localStorage.setItem(this.STORAGE_KEYS.CONFIG, JSON.stringify(config));
  },

  getAllAssignments() {
    const data = localStorage.getItem(this.STORAGE_KEYS.ASSIGNMENTS);
    return data ? JSON.parse(data) : [];
  },

  getAssignment(id) {
    const assignments = this.getAllAssignments();
    return assignments.find(a => a.id === id) || null;
  },

  saveAssignment(assignment) {
    const assignments = this.getAllAssignments();
    const existingIndex = assignments.findIndex(a => a.id === assignment.id);
    
    if (existingIndex >= 0) {
      assignments[existingIndex] = { ...assignment, updatedAt: new Date().toISOString() };
    } else {
      assignments.push({
        ...assignment,
        id: assignment.id || this.generateId('ASSIGN'),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    localStorage.setItem(this.STORAGE_KEYS.ASSIGNMENTS, JSON.stringify(assignments));
    this.clearWorkloadCache();
    return assignment;
  },

  deleteAssignment(id) {
    const assignments = this.getAllAssignments().filter(a => a.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.ASSIGNMENTS, JSON.stringify(assignments));
    this.clearWorkloadCache();
  },

  getAssignmentsForUser(userId, status = 'all') {
    const assignments = this.getAllAssignments();
    let filtered = assignments.filter(a => a.assignedTo === userId);
    
    if (status !== 'all') {
      filtered = filtered.filter(a => a.status === status);
    }
    
    return filtered.sort((a, b) => {
      const aDue = a.dueDate ? new Date(a.dueDate) : new Date(9999999999999);
      const bDue = b.dueDate ? new Date(b.dueDate) : new Date(9999999999999);
      return aDue - bDue;
    });
  },

  getAssignmentsByUser(userId, status = 'all') {
    const assignments = this.getAllAssignments();
    let filtered = assignments.filter(a => a.assignedBy === userId);
    
    if (status !== 'all') {
      filtered = filtered.filter(a => a.status === status);
    }
    
    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  getWorkloadForUser(userId) {
    const cache = this.getWorkloadCache();
    if (cache[userId]) {
      const cached = cache[userId];
      if (Date.now() - cached.timestamp < 60000) { // 1 minute cache
        return cached.workload;
      }
    }
    
    const assignments = this.getAssignmentsForUser(userId, 'active');
    const config = this.getConfig();
    const mode = config?.workloadThresholds?.mode || 'count';
    
    let workload;
    if (mode === 'points') {
      const points = assignments.reduce((sum, a) => {
        const itemType = a.itemType || 'customTask';
        const pointValue = config.workloadThresholds.taskPoints[itemType] || 5;
        return sum + pointValue;
      }, 0);
      workload = {
        count: assignments.length,
        points,
        level: this.getWorkloadLevel(points, config.workloadThresholds.pointsThresholds)
      };
    } else {
      workload = {
        count: assignments.length,
        points: 0,
        level: this.getWorkloadLevel(assignments.length, config?.workloadThresholds?.countThresholds || { yellow: 8, red: 12, critical: 15 })
      };
    }
    
    this.setWorkloadCache(userId, workload);
    return workload;
  },

  getWorkloadLevel(value, thresholds) {
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.red) return 'red';
    if (value >= thresholds.yellow) return 'yellow';
    return 'green';
  },

  getWorkloadCache() {
    const data = localStorage.getItem(this.STORAGE_KEYS.WORKLOAD_CACHE);
    return data ? JSON.parse(data) : {};
  },

  setWorkloadCache(userId, workload) {
    const cache = this.getWorkloadCache();
    cache[userId] = { workload, timestamp: Date.now() };
    localStorage.setItem(this.STORAGE_KEYS.WORKLOAD_CACHE, JSON.stringify(cache));
  },

  clearWorkloadCache() {
    localStorage.removeItem(this.STORAGE_KEYS.WORKLOAD_CACHE);
  },

  updateAssignmentStatus(id, status, declineReason = null) {
    const assignment = this.getAssignment(id);
    if (!assignment) return null;
    
    assignment.status = status;
    if (status === 'completed') {
      assignment.completedAt = new Date().toISOString();
    }
    if (status === 'declined' && declineReason) {
      assignment.declineReason = declineReason;
    }
    
    return this.saveAssignment(assignment);
  },

  canAssignItemType(userRole, itemType) {
    const config = this.getConfig();
    const permissions = config?.assignmentPermissions?.[userRole];
    if (!permissions) return false;
    
    if (permissions.canAssign?.all) return true;
    
    const itemTypeMap = {
      'priorityQueueTask': 'priorityQueueTasks',
      'qualityAlert': 'qualityAlerts',
      'staffingAlert': 'staffingAlerts',
      'accessIssue': 'accessIssues',
      'inventory': 'inventory',
      'initiativeMilestone': 'initiativeMilestones',
      'customTask': 'customTasks'
    };
    
    const key = itemTypeMap[itemType] || itemType;
    return permissions.canAssign?.[key] === true;
  },

  isItemAssignable(itemType, itemData, config = null) {
    const cfg = config || this.getConfig();
    const rules = cfg?.assignmentRules?.[itemType] || cfg?.assignmentRules?.customTasks;
    if (!rules || !rules.enabled) return false;
    
    // Simple check: if item has overdue/at-risk flags, it's assignable
    // More complex logic can be added based on overdueDefinition/atRiskDefinition
    return itemData.isOverdue === true || itemData.isAtRisk === true;
  },

  generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
};

AssignmentDB.init();
